import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSearch, FiFilter, FiCheck, FiCheckSquare, FiSquare, FiList, FiGrid } = FiIcons;

const ItemSelectionFilter = ({ questions, selectedQuestions, setSelectedQuestions }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  
  // Get unique subjects and types for filters
  const subjects = [...new Set(questions.map(q => q.subject).filter(Boolean))];
  const types = [...new Set(questions.map(q => q.type).filter(Boolean))];
  
  // Filter questions based on selected filters and search query
  const filteredQuestions = questions.filter(question => {
    const matchesSubject = !filterSubject || question.subject === filterSubject;
    const matchesType = !filterType || question.type === filterType;
    const matchesSearch = !searchQuery || 
      question.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.type?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSubject && matchesType && matchesSearch;
  });

  // Select or deselect all filtered questions
  const toggleSelectAll = () => {
    if (isAllSelected()) {
      // Deselect all filtered questions
      const filteredIds = new Set(filteredQuestions.map(q => q.id));
      setSelectedQuestions(selectedQuestions.filter(id => !filteredIds.has(id)));
    } else {
      // Select all filtered questions
      const newSelectedIds = [...selectedQuestions];
      filteredQuestions.forEach(question => {
        if (!newSelectedIds.includes(question.id)) {
          newSelectedIds.push(question.id);
        }
      });
      setSelectedQuestions(newSelectedIds);
    }
  };
  
  // Check if all filtered questions are selected
  const isAllSelected = () => {
    return filteredQuestions.length > 0 && 
      filteredQuestions.every(q => selectedQuestions.includes(q.id));
  };
  
  // Toggle selection of a specific question
  const toggleSelectQuestion = (questionId) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };
  
  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setFilterSubject('');
    setFilterType('');
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div className="mb-4 md:mb-0">
          <h3 className="text-lg font-semibold text-gray-800">Question Selection</h3>
          <p className="text-sm text-gray-600">
            Select specific questions for analysis ({selectedQuestions.length} of {questions.length} selected)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            onClick={resetFilters}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center space-x-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiFilter} className="text-gray-600" />
            <span>Reset Filters</span>
          </motion.button>
          <div className="flex bg-gray-100 rounded-lg overflow-hidden">
            <motion.button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}
              whileHover={{ backgroundColor: viewMode === 'list' ? '' : 'rgba(0,0,0,0.05)' }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiList} />
            </motion.button>
            <motion.button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}
              whileHover={{ backgroundColor: viewMode === 'grid' ? '' : 'rgba(0,0,0,0.05)' }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiGrid} />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Search field */}
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Subject filter */}
        <div>
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>

        {/* Question type filter */}
        <div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Question Types</option>
            {types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Select all checkbox */}
      <div className="flex items-center mb-4 p-2 bg-gray-50 rounded-lg">
        <motion.button
          onClick={toggleSelectAll}
          className="flex items-center space-x-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon 
            icon={isAllSelected() ? FiCheckSquare : FiSquare} 
            className={isAllSelected() ? "text-primary-600" : "text-gray-400"} 
          />
          <span className="text-sm font-medium">
            {isAllSelected() ? 'Deselect All' : 'Select All'} ({filteredQuestions.length} questions)
          </span>
        </motion.button>
      </div>

      {/* Questions list */}
      {filteredQuestions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <SafeIcon icon={FiFilter} className="mx-auto text-4xl text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Questions Match Your Filters</h3>
          <p className="text-gray-600 mt-2">Try adjusting your search criteria or filters</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
          {filteredQuestions.map(question => (
            <motion.div
              key={question.id}
              onClick={() => toggleSelectQuestion(question.id)}
              className={`flex items-start p-3 rounded-lg cursor-pointer border ${
                selectedQuestions.includes(question.id) 
                  ? 'bg-primary-50 border-primary-300' 
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <SafeIcon 
                icon={selectedQuestions.includes(question.id) ? FiCheckSquare : FiSquare} 
                className={`mt-1 mr-3 flex-shrink-0 ${
                  selectedQuestions.includes(question.id) ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 line-clamp-2">{question.text}</p>
                <div className="flex items-center mt-2 space-x-3">
                  {question.subject && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {question.subject}
                    </span>
                  )}
                  {question.type && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {question.type}
                    </span>
                  )}
                  {question.marks && (
                    <span className="text-xs text-gray-500">
                      {question.marks} marks
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-80 overflow-y-auto pr-2">
          {filteredQuestions.map(question => (
            <motion.div
              key={question.id}
              onClick={() => toggleSelectQuestion(question.id)}
              className={`p-4 rounded-lg cursor-pointer border ${
                selectedQuestions.includes(question.id) 
                  ? 'bg-primary-50 border-primary-300' 
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start mb-3">
                <SafeIcon 
                  icon={selectedQuestions.includes(question.id) ? FiCheckSquare : FiSquare} 
                  className={`mr-2 ${
                    selectedQuestions.includes(question.id) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                <p className="text-sm font-medium text-gray-800 line-clamp-2">{question.text}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {question.subject && (
                  <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                    {question.subject}
                  </span>
                )}
                {question.type && (
                  <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                    {question.type}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
      
      <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
        <div>
          {filteredQuestions.length} of {questions.length} questions shown
        </div>
        <div>
          {selectedQuestions.length} questions selected for analysis
        </div>
      </div>
    </div>
  );
};

export default ItemSelectionFilter;